/**
 * **전송** — 우리 ES route 하나를 부르는 함수를 만든다. 무엇을 묻는지는 모른다.
 *
 * ```
 *   actions/els/moment.ts 질문 — op를 알고 결과를 도메인 타입으로 확인한다
 *     │ createElsProxyRequest(path)
 *   이 파일              전송 — in-flight dedupe · 2초 응답 캐시 · 트레이싱 헤더
 *     ▼ POST /api/…
 *   app/api/…/route      디스패치 (⚠️ 검증은 하지 않는다 — 아래 참조)
 * ```
 *
 * ## ⛔ ES route 는 로그인 토큰이 필수다
 * `api/els/*` 는 `withUser` 로 닫혀 있어 이 파일이 **모든 요청에 Bearer ID 토큰을
 * 붙인다**(`fetchIdToken`). 토큰이 없으면(무로그인) 서버가 401 로 거부한다 — 무로그인
 * 화면은 공개 창구(`/api/widget-data/keyword-posts`)만 쓸 수 있다. `x-user-id` 헤더는
 * 에러 로그용일 뿐 인증이 아니다. 만료 차단(`assertElsAccess`)은 그 위의 **화면 게이트**다 —
 * 만료 «구독»은 토큰만으로 못 거르므로 여기서 먼저 끊는다(서버측 강제는 `server/auth/subscription-gate`).
 *
 * ## 공용이다 — 인덱스에 묶여 있지 않다
 * 경로를 인자로 받는다 — `moments`·`influencers`·`product-rankings`·`ads-meta` 넷과
 * 공개 위젯 창구가 쓴다. 인덱스가 늘면 여기를 같이 쓴다.
 *
 * ## ⚠️ 재시도는 여기 없다
 * ES 429는 서버가 받는데 재시도를 브라우저가 하면 왕복이 두 배다. `lib/els/els-connection`이
 * 한다 — 여기서 다시 하면 **시도 횟수가 곱해진다.**
 *
 * ## ⚠️ `src/lib/axios.ts`(공용 인스턴스)를 쓰지 않는다
 * `baseURL`이 걸려 있어 상대경로에 맞지 않고, 응답 인터셉터가 에러를 `error.response.data`로
 * 바꿔 **`error.response.status`가 사라진다.** 소비처가 status로 실패를 가른다(502 처리).
 */import type { AxiosResponse } from 'axios';

import axios from 'axios';

import { logElsTrace } from 'src/lib/els/els-trace';
import { assertElsAccess } from 'src/lib/els/els-access-gate';
import { fetchIdToken, getCurrentUserId } from 'src/lib/firebase/auth-kit';

/**
 * ES route 에 보내는 요청 본문. 창구마다 모양이 달라 구조를 고정하지 않는다 —
 * 무엇이 유효한 본문인지는 받는 route 와 `server/<도메인>/` 이 책임진다.
 */

export type ElsRequestBody = Record<string, unknown>;

/** ES 응답. 소비 코드가 실제로 읽는 갈래만 적는다 */
export type ElsResponseBody = {
  took?: number;
  hits?: {
    total?: number | { value?: number; relation?: string };
    hits?: {
      _id?: string;
      _source?: Record<string, unknown>;
      sort?: (string | number)[];
    }[];
  };
  aggregations?: Record<string, unknown>;
  suggest?: Record<string, unknown>;
};

export type ElsResponse = AxiosResponse<ElsResponseBody>;

function getElsProxyTracingHeaders(): Record<string, string> {
  // 현재 사용자 ID — 로그인 전이면 null. 에러 로그 상관용일 뿐 인증이 아니다(인증은 Bearer).
  const userId = getCurrentUserId();

  // 현재 페이지 경로 가져오기
  let pagePath: string | null = null;
  try {
    if (typeof window !== 'undefined' && window.location) {
      pagePath = window.location.pathname;
    }
  } catch {
    // window.location 접근 실패 시 무시
  }

  return {
    ...(userId && { 'x-user-id': userId }),
    ...(pagePath && { 'x-page-path': pagePath }),
  };
}

/**
 * ⛔ 이 전송기는 **브라우저 전용**이다 — 서버에서 부르면 던진다.
 *
 * 서버 코드는 `server/<도메인>/` → `els-connection` 으로 ES 를 직접 친다. 서버가 자기 호스트
 * 절대 URL 로 자기 API 를 되부르는 루프백은 **기각된 설계**라, 조용히 통과시키지 않고
 * **소리 나게** 끊는다(안 그러면 「서버에서도 되나 보다」로 오독된다).
 */
function assertBrowserOnly(): void {
  if (typeof window === 'undefined') {
    throw new Error(
      '[els-request] 브라우저 전용 전송기다 — 서버에서는 server/<도메인>/ 함수를 직접 불러라(A526)',
    );
  }
}

/** 로그인 토큰 헤더 — 없으면 빈 객체(서버가 401 로 거른다) */
async function getElsAuthHeader(): Promise<Record<string, string>> {
  const token = await fetchIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 동일 payload 동시 호출 시 1회만 전송 (in-flight dedupe)
const inFlightElsRequestMap = new Map<string, Promise<ElsResponse>>();
/**
 * 2초 응답 캐시. 넣을 때 지우고 다시 넣어 **삽입 순서 = 받은 시각 순서**를 지킨다 —
 * 그래서 만료 걷기는 앞에서부터 첫 유효 항목까지만 돈다.
 */
const recentElsResponseMap = new Map<string, { ts: number; response: ElsResponse }>();
const RECENT_ELS_RESPONSE_TTL_MS = 2000;

const isRecentEntryFresh = (entry: { ts: number }, now: number): boolean =>
  now - entry.ts < RECENT_ELS_RESPONSE_TTL_MS;

/** 앞(오래된 쪽)부터 만료 항목을 지운다 — 첫 유효 항목에서 멈춘다 */
function sweepExpiredElsResponses(now: number): void {
  for (const [key, entry] of recentElsResponseMap) {
    if (isRecentEntryFresh(entry, now)) return;
    recentElsResponseMap.delete(key);
  }
}

function rememberElsResponse(key: string, response: ElsResponse): void {
  const now = Date.now();
  sweepExpiredElsResponses(now);
  recentElsResponseMap.delete(key);
  recentElsResponseMap.set(key, { ts: now, response });
}

/** 2초 응답 캐시에 남은 항목 수 — 만료 항목이 쌓이지 않는지 보는 진단·테스트용 */
export function readRecentElsResponseCacheSize(): number {
  return recentElsResponseMap.size;
}

/**
 * 우리 API의 ES 경로 하나를 부르는 함수를 만든다 — dedupe · 2초 캐시 · 트레이싱 헤더.
 *
 * ⚠️ 맵은 **모듈 전역 하나**이고 키에 경로가 들어간다. 경로마다 맵을 만들면
 * 같은 payload 가 ES 에 두 번 나간다 — 이 파일이 존재하는 이유가 사라진다.
 *
 * ⚠️ **재시도는 여기 없다** — `lib/els/els-connection`이 한다(파일 머리말 참조).
 */
export type ElsProxyCallOptions = {
  /**
   * 요청을 도중에 끊는다 — 화면이 필터를 빠르게 바꿀 때 앞선 조회를 버리는 용도.
   *
   * ⚠️ **주면 dedupe·2초 캐시를 타지 않는다.** 공유 promise를 한 호출자가 끊으면 같은
   * payload를 기다리던 다른 호출자까지 함께 죽는다. 끊을 수 있는 요청은 남과 나눠 쓸 수
   * 없다는 뜻이라 둘 중 하나를 고르게 한다.
   */
  signal?: AbortSignal;
};

/**
 * 창구를 만들 때 정하는 것.
 * `allowExpired` — 만료 화면 전용 창구(`/api/els/moments/expired-summary`)만 참이다. 만료 관문(`assertElsAccess`)을
 * 건너뛴다 — 그 창구는 서버가 소속·만료를 직접 본다. ⚠️ 다른 창구에 켜면 만료 뒤에도 조회가 나간다.
 */
export type ElsProxyRequestConfig = { allowExpired?: boolean };

export function createElsProxyRequest(path: string, config: ElsProxyRequestConfig = {}) {
  return async (
    payload: ElsRequestBody,
    options: ElsProxyCallOptions = {},
  ): Promise<ElsResponse> => {
    // 만료된 워크스페이스는 여기서 끊는다.
    // ⚠️ signal 분기보다 **앞**에 둬야 취소가능 경로까지 함께 걸린다.
    //    dedupe 캐시보다도 앞이라 만료 후 캐시된 응답이 재사용되지도 않는다.
    if (!config.allowExpired) assertElsAccess();

    // ⚠️ 끊을 수 있는 요청은 공유하지 않는다(위 ⚠️)
    if (options.signal) {
      return sendElsProxyPayload(path, payload, options.signal);
    }

    const requestKey = `${path}\u0001${JSON.stringify(payload ?? {})}`;
    const now = Date.now();
    sweepExpiredElsResponses(now);
    const recentEntry = recentElsResponseMap.get(requestKey);
    if (recentEntry && isRecentEntryFresh(recentEntry, now)) {
      logElsTrace('🧠 [els] 동일 payload 최근 응답 재사용');
      return recentEntry.response;
    }
    // 걷기가 순서 어긋남으로 멈춰도 이 열쇠의 만료 항목은 여기서 지운다
    if (recentEntry) recentElsResponseMap.delete(requestKey);

    const existingRequest = inFlightElsRequestMap.get(requestKey);
    if (existingRequest) {
      logElsTrace('♻️ [els] 동일 payload in-flight 재사용');
      return existingRequest;
    }

    logElsTrace('📦 요청 페이로드:', JSON.stringify(payload, null, 2));

    assertBrowserOnly();
    const tracingHeaders = getElsProxyTracingHeaders();

    const requestPromise = (async (): Promise<ElsResponse> => {
      const response = await axios.post<ElsResponseBody>(
        path,
        payload,
        {
          timeout: 180000,
          headers: {
            'Content-Type': 'application/json',
            ...tracingHeaders,
            ...(await getElsAuthHeader()),
          },
        }
      );
      logElsTrace('📦 [els] 응답:', response);
      rememberElsResponse(requestKey, response);
      return response;
    })().finally(() => {
      inFlightElsRequestMap.delete(requestKey);
    });


    inFlightElsRequestMap.set(requestKey, requestPromise);
    return requestPromise;
  };
}

/**
 * 끊을 수 있는 한 방 — dedupe·캐시를 거치지 않는다.
 *
 * ⚠️ 취소를 `axios`는 `CanceledError`(`name === 'CanceledError'`)로 던지는데, 화면은
 * **`name === 'AbortError'`** 로 취소를 가린다 — 그래서 여기서 이름을 `AbortError` 로 맞춰
 * 던진다. 안 맞추면 사용자가 필터를 바꿀 때마다 "데이터를 불러오지 못했습니다"가 뜬다.
 */
async function sendElsProxyPayload(
  path: string,
  payload: ElsRequestBody,
  signal: AbortSignal,
): Promise<ElsResponse> {
  logElsTrace('📦 요청 페이로드(취소가능):', JSON.stringify(payload, null, 2));

  assertBrowserOnly();

  try {
    return await axios.post<ElsResponseBody>(path, payload, {
      timeout: 180000,
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...getElsProxyTracingHeaders(),
        ...(await getElsAuthHeader()),
      },
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      const aborted = new Error('요청이 취소되었습니다.');
      aborted.name = 'AbortError';
      throw aborted;
    }
    throw error;
  }
}
